import apiClient, { getApiErrorInfo } from './api';
import type { Registration } from './registrationService';
import { track } from './analytics';
import { CourseType } from './courseService';

export interface WebinarRegistrationPayload {
  name: string;
  email: string;
  phone: string;
  courseInstanceId?: number;
}

export interface WebinarRegistrationResult {
  success: boolean;
  status: number | null;
  registration?: Registration;
  message: string;
  errorCode?: string;
}

const getWebinarRegistrationMessage = (status: number | null, fallbackMessage: string): string => {
  switch (status) {
    case 400:
      return 'Your webinar registration details are invalid. Please review and try again.';
    case 401:
      return 'Your session has expired. Please sign in and try again.';
    case 403:
      return 'Your subscription does not include webinar access.';
    case 404:
      return 'This webinar could not be found. Please refresh and try again.';
    case 409:
      return fallbackMessage;
    case 500:
      return 'The webinar provider returned an error. Please try again in a few minutes.';
    default:
      return fallbackMessage;
  }
};

const getWebinarCancellationMessage = (status: number | null, fallbackMessage: string): string => {
  switch (status) {
    case 401:
      return 'Your session has expired. Please sign in and try again.';
    case 404:
      return 'No active webinar registration was found to cancel.';
    case 500:
      return 'We could not cancel your webinar registration right now. Please try again in a few minutes.';
    default:
      return fallbackMessage;
  }
};

class WebinarRegistrationService {
  async registerForWebinar(courseId: number, payload: WebinarRegistrationPayload): Promise<WebinarRegistrationResult> {
    try {
      const response = await apiClient.post<Registration>(`/api/registrations/${courseId}/webinar`, payload);
      track('course_registered', { course_id: String(courseId), course_type: CourseType.Webinar });
      return {
        success: true,
        status: response.status,
        registration: response.data,
        message: 'You are successfully registered for this webinar.',
      };
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      return {
        success: false,
        status: apiError.status,
        message: getWebinarRegistrationMessage(apiError.status, apiError.message),
        errorCode: apiError.code,
      };
    }
  }

  async transferWebinarRegistration(courseId: number, payload: WebinarRegistrationPayload): Promise<WebinarRegistrationResult> {
    try {
      const response = await apiClient.post<Registration>(`/api/registrations/${courseId}/webinar/transfer`, payload);
      return {
        success: true,
        status: response.status,
        registration: response.data,
        message: 'Your registration has been transferred to the new date.',
      };
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      return {
        success: false,
        status: apiError.status,
        message: getWebinarRegistrationMessage(apiError.status, apiError.message),
        errorCode: apiError.code,
      };
    }
  }

  async cancelWebinarRegistration(courseId: number): Promise<WebinarRegistrationResult> {
    try {
      const response = await apiClient.post<Registration>(`/api/registrations/${courseId}/webinar/cancel`);
      return {
        success: true,
        status: response.status,
        registration: response.data,
        message: 'Your webinar registration has been cancelled.',
      };
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      return {
        success: false,
        status: apiError.status,
        message: getWebinarCancellationMessage(apiError.status, apiError.message),
        errorCode: apiError.code,
      };
    }
  }
}

export const webinarRegistrationService = new WebinarRegistrationService();
export default webinarRegistrationService;